import React, { useState, Fragment } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Stat } from '../../../models/interfaces/Stat';
import { RosterEntry, isRosterEntry } from '../../../models/interfaces/RosterEntry';
import { isMatchEntry } from '../../../models/interfaces/MatchEntry';
import { parseTeamColor } from '../../../helpers/parseTeamColor';
import { PerformerTypes } from '../../../models/interfaces/Stat';
import { Row, Col, Button, Card, Spinner } from 'react-bootstrap';
import { MatchEntry } from '../../../models/interfaces/MatchEntry';
import styled from 'styled-components';
import '../../../components/styles/transitionEffectStyles.css';

const StyledCard = styled(Card)`
    box-sizing: border-box;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    ${({ $color }) =>
        $color &&
        'box-shadow: inset 0.5rem 0 0 0 ' +
            $color +
            // re-adds bootstraps "shadow-sm" properties to make sure they are not overwritten
            ', 0 .125rem .25rem rgba(0,0,0,.075) !important;'}
`;

export function playerStatStringSummary(player: RosterEntry): string {
    return `#${player.capNumber.displayName} ${player.name.firstName} ${player.name.lastName}, ${player.team.name}`;
}

export function teamStatStringSummary(team: MatchEntry): string {
    return `${team.name}`;
}

export function performerString(stat: Stat): string {
    if (stat.description.supportedPerfomers.has(PerformerTypes.player) && isRosterEntry(stat.performer)) {
        return playerStatStringSummary(stat.performer as RosterEntry);
    } else if (stat.description.supportedPerfomers.has(PerformerTypes.team) && isMatchEntry(stat.performer)) {
        return teamStatStringSummary(stat.performer as MatchEntry);
    }
    // default return also captures tableworker as we don't want to show any specific text for them
    return '';
}

export function minutesAndSeconds(totalSeconds: number) {
    let minutes = Math.floor(totalSeconds / 60);
    let seconds = totalSeconds % 60;
    return `${minutes.toLocaleString('en-US', {
        minimumIntegerDigits: 2,
    })}:${seconds.toLocaleString('en-US', { minimumIntegerDigits: 2 })}`;
}

interface Props {
    lastStat: Stat | undefined;
    handleEditStat: () => void;
    handleDeleteStat: (stat: Stat, resetState: () => void) => void;
    titleString?: string;
    editMatchStats: boolean;
    showUpdateTime: boolean
}

const DisplaySingleStat = ({
    lastStat,
    handleEditStat,
    handleDeleteStat,
    titleString,
    editMatchStats,
    showUpdateTime
}: Props) => {
    const [confirmDeletingStat, setConfirmDeletingStat] = useState(false);
    const [deletingStat, setDeletingStat] = useState(false);

    const handleConfirmDeletingStat = () => {
        setConfirmDeletingStat(true);
    };
    const resetConfirmDeletingStat = () => {
        setConfirmDeletingStat(false);
    };

    const clearDeletingStatState = () => {
        setDeletingStat(false);
    };

    const deleteStat = () => {
        if (lastStat) {
            setDeletingStat(true);
            handleDeleteStat(lastStat, clearDeletingStatState);
        }
    };

    return (
        <CSSTransition in={!!lastStat} timeout={300} classNames="display" unmountOnExit>
            {/* fragment is needed as the above transition component needs a child component. Without this fragment when a stat was deleted an error occurred as no children would be provided */}
            <Fragment>
                {lastStat && (
                    <StyledCard color={isMatchEntry(lastStat.performer) ?
                                                        parseTeamColor(lastStat.performer.color)
                                                        : isRosterEntry(lastStat.performer) ?
                                                            parseTeamColor(lastStat.performer.team.color):
                                                            ''} className="my-2 shadow-sm">
                        <Card.Body className="py-0">
                            <Row className="my-2 justify-content-between align-items-center">
                                <Col>
                                    {titleString && (
                                        <Row className="my-1">
                                            <Col>
                                                <i>{titleString}</i>
                                            </Col>
                                        </Row>
                                    )}
                                    <Row className="my-2">
                                        <Col>
                                            <Row>
                                                <Col>
                                                    <strong>{lastStat.matchSegment.displayName} {minutesAndSeconds(lastStat.timestamp)}</strong>
                                                    <br />
                                                    <span className="text-monospace">{lastStat.displayName}</span>
                                                    { showUpdateTime ? (
                                                        <><br />
                                                            <small>
                                                            Created: {lastStat.creationTime.toLocaleTimeString([], {
                                                                    hour: '2-digit',
                                                                    minute: '2-digit',
                                                                    })}
                                                            { lastStat.lastUpdateTime ? 
                                                                `, Updated: ${lastStat.lastUpdateTime.toLocaleTimeString([], {
                                                                    hour: '2-digit',
                                                                    minute: '2-digit',
                                                                })}`
                                                            : ''}
                                                            </small></>) 
                                                        : <></>}
                                                </Col>
                                                <Col>
                                                    {performerString(lastStat)}
                                                </Col>
                                            </Row>
                                            {lastStat.remarks && (
                                                <Row>
                                                    <Col>
                                                        Notes:{' '}
                                                        <span style={{ fontStyle: 'italic' }}>
                                                            {lastStat.remarks}
                                                        </span>
                                                    </Col>
                                                </Row>
                                            )}
                                        </Col>
                                    </Row>
                                </Col>
                                <Col xs="auto">
                                    {confirmDeletingStat ? (
                                        <>
                                            <Row className="justify-content-end my-2">
                                                <Col xs="auto">
                                                    <Button variant="danger" onClick={deleteStat}>
                                                        {deletingStat ? (
                                                            <Spinner
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                role="status"
                                                                aria-hidden="true"
                                                            />
                                                        ) : (
                                                            'Confirm'
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                            <Row className="justify-content-end my-2">
                                                <Col xs="auto">
                                                    <Button
                                                        variant="outline-primary"
                                                        onClick={resetConfirmDeletingStat}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </>
                                    ) : (
                                        editMatchStats && (
                                            <>
                                                <Row className="justify-content-end my-2">
                                                    <Col xs="auto">
                                                        <Button
                                                            variant="outline-danger"
                                                            onClick={handleConfirmDeletingStat}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Col>
                                                </Row>

                                                <Row className="justify-content-end my-2">
                                                    <Col xs="auto">
                                                        <Button
                                                            variant="outline-secondary"
                                                            onClick={handleEditStat}
                                                        >
                                                            Edit
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </>
                                        )
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </StyledCard>
                )}
            </Fragment>
        </CSSTransition>
    );
};

export default DisplaySingleStat;
